const API = process.env.REACT_APP_API_URL || 'https://nico-548x.onrender.com/api';

let isRefreshing = false;

async function tryRefreshToken() {
  if (isRefreshing) return false;
  isRefreshing = true;
  try {
    const refresh = localStorage.getItem('nico_refresh');
    if (!refresh) return false;
    const r = await fetch(`${API}/auth/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
    if (!r.ok) return false;
    const d = await r.json();
    localStorage.setItem('nico_token', d.token);
    return true;
  } catch { return false; }
  finally { isRefreshing = false; }
}

const req = async (method, path, body, retried = false) => {
  const token = localStorage.getItem('nico_token');
  const r = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const d = await r.json();

  // Auto-refresh on 401 (token expired)
  if (r.status === 401 && !retried) {
    const refreshed = await tryRefreshToken();
    if (refreshed) return req(method, path, body, true);
    localStorage.removeItem('nico_token');
    localStorage.removeItem('nico_refresh');
    window.location.href = '/login';
    throw new Error('Sessão expirada');
  }

  if (!r.ok) { const err = new Error(d.error || 'Erro'); err.data = d; throw err; }
  return d;
};

export const vehiclesAPI = {
  list: () => req('GET', '/vehicles'),
  get: (id) => req('GET', `/vehicles/${id}`),
  create: (data) => req('POST', '/vehicles', data),
  update: (id, data) => req('PUT', `/vehicles/${id}`, data),
  delete: (id) => req('DELETE', `/vehicles/${id}`),
  report: (id) => req('GET', `/vehicles/${id}/report`),
  // [NEW] veículos que o usuário já repassou pra outra conta (só leitura)
  sold: () => req('GET', '/vehicles/vendidos'),
};

export const servicesAPI = {
  types: () => req('GET', '/services/types'),
  create: (data) => req('POST', '/services', data),
  update: (id, data) => req('PUT', `/services/${id}`, data),
  validate: (id, action) => req('PUT', `/services/${id}/validate`, { action }),
  delete: (id) => req('DELETE', `/services/${id}`),
  // [NEW] Foto/vídeo de comprovação do serviço — pede uma URL assinada do Supabase
  // Storage. O arquivo em si é enviado direto pra lá (PUT na uploadUrl), não passa por aqui.
  getUploadUrl: (contentType) => req('POST', '/services/media/upload-url', { contentType }),
  // [NEW] Resolve um código curto de mídia (usenico.com.br/m/<código>, ver
  // MediaLinkPage.jsx) pra URL real. Rota pública no backend — funciona com ou sem
  // login, já que quem abre o link pode nem ter conta no NICO.
  resolveMediaLink: (code) => req('GET', `/services/media/link/${code}`),
};

export const alertsAPI = {
  list: () => req('GET', '/alerts'),
  dismiss: (id) => req('PUT', `/alerts/${id}/dismiss`),
  reactivate: (id) => req('PUT', `/alerts/${id}/reactivate`),
};

export const paymentsAPI = {
  config: () => req('GET', '/payments/config'),
  vehicleAccess: () => req('GET', '/payments/vehicle-access'),
  createCheckout: (vehicleId) => req('POST', '/payments/create-checkout', { vehicle_id: vehicleId }),
  cancel: (vehicleId) => req('POST', '/payments/cancel', { vehicle_id: vehicleId }),
  subscriptions: () => req('GET', '/payments/subscriptions'),
};

// [NEW] Repasse de veículo entre contas (venda fora do app — a NICO nunca media a
// venda em si, só move o registro de histórico quando o dono atual autoriza e quem
// recebe confirma). Ver backend/routes/transfers.js.
export const transfersAPI = {
  // Dono atual gera um link/código pra mandar pro comprador (WhatsApp, etc).
  initiate: (vehicleId) => req('POST', '/transfers', { vehicle_id: vehicleId }),
  // Dono atual cancela um link pendente (ex: quer gerar outro, ou desistiu).
  cancelPending: (vehicleId) => req('DELETE', `/transfers/${vehicleId}`),
  // Preview público do que está sendo repassado — chamado ANTES de logar, então não
  // manda token de auth (a rota no backend não exige authenticate).
  preview: (token) => req('GET', `/transfers/${token}`),
  // Quem recebe confirma, já autenticado.
  accept: (token) => req('POST', `/transfers/${token}/accept`),
};

export const interestsAPI = {
  // Registrar o próprio interesse continua sendo ação de usuário comum (token normal).
  register: (serviceKey) => req('POST', '/interests', { service_key: serviceKey }),
  // [FIX] stats/markRead/markUnread agora exigem admin no backend (server.js —
  // requireAdminAuth) — precisam ir pelo adminReq (token de sessionStorage), senão
  // voltam 401. Antes essas rotas eram públicas sem autenticação nenhuma; ver auditoria
  // de segurança de 28/ago/2026, achado crítico #1.
  stats: () => adminReq('GET', '/interests/stats'),
  markRead: (id) => adminReq('PUT', `/interests/${id}/read`),
  markUnread: (id) => adminReq('PUT', `/interests/${id}/unread`),
};

// [FIX] list/markRead agora exigem admin no backend — mesma correção do interestsAPI
// acima. Antes eram públicas sem autenticação (achado crítico #2 da auditoria).
export const contactsAPI = {
  list: () => adminReq('GET', '/contacts'),
  markRead: (id) => adminReq('PUT', `/contacts/${id}/read`),
};

// [FIX] list/update agora exigem admin no backend — mesma correção acima. O update
// (reescrever conteúdo de um template de alerta) era o achado crítico #3 da auditoria:
// qualquer pessoa na internet conseguia editar templates sem login nenhum.
export const alertTemplatesAPI = {
  list: () => adminReq('GET', '/alert-templates'),
  update: (id, data) => adminReq('PUT', `/alert-templates/${id}`, data),
};

export const professionalsAPI = {
  search: (params) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/professionals/search?${qs}`);
  },
  me: () => req('GET', '/professionals/me'),
  updateMe: (data) => req('PUT', '/professionals/me', data),
  create: (data) => req('POST', '/professionals', data),
  pending: () => req('GET', '/professionals/me/pending'),
  get: (id) => req('GET', `/professionals/${id}`),
};

export const authAPI = {
  me: () => req('GET', '/auth/me'),
  forgotPassword: (email) => req('POST', '/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => req('POST', '/auth/reset-password', { token, new_password }),
  updateProfile: (data) => req('PUT', '/auth/profile', data),
  updatePassword: (data) => req('PUT', '/auth/password', data),
};

// Admin API — usa token de admin do sessionStorage
const adminReq = async (method, path, body) => {
  const token = sessionStorage.getItem('nico_admin_token');
  const r = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const d = await r.json();
  if (!r.ok) { const err = new Error(d.error || 'Erro'); err.data = d; throw err; }
  return d;
};

export const adminAPI = {
  login: (email, password) => {
    return fetch(`${API}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }).then(async r => {
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Credenciais inválidas');
      return d;
    });
  },
  stats: () => adminReq('GET', '/admin/stats'),
  users: (params) => {
    const qs = new URLSearchParams(params).toString();
    return adminReq('GET', `/admin/users?${qs}`);
  },
  updateUserStatus: (id, status) => adminReq('PUT', `/admin/users/${id}/status`, { status }),
  verifyProfessional: (id) => adminReq('PUT', `/admin/professionals/${id}/verify`),
  searchVehicles: (q) => adminReq('GET', `/admin/vehicles/search?q=${encodeURIComponent(q)}`),
  professionals: (params) => {
    const qs = new URLSearchParams(params).toString();
    return adminReq('GET', `/admin/professionals?${qs}`);
  },
  subscriptions: (params) => {
    const qs = new URLSearchParams(params).toString();
    return adminReq('GET', `/admin/subscriptions?${qs}`);
  },
  cancelSubscription: (id) => adminReq('POST', `/admin/subscriptions/${id}/cancel`),
  plateConflicts: (params) => {
    const qs = new URLSearchParams(params).toString();
    return adminReq('GET', `/admin/plate-conflicts?${qs}`);
  },
  resolvePlateConflict: (id, resolution) => adminReq('POST', `/admin/plate-conflicts/${id}/resolve`, { resolution }),
  reports: () => adminReq('GET', '/admin/reports'),
  audit: () => adminReq('GET', '/admin/audit'),
  features: () => adminReq('GET', '/admin/features/all'),
  toggleFeature: (key, enabled) => adminReq('PUT', `/admin/features/${key}`, { enabled }),
};
